import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import apiService from 'services/api'

import { MobileModal } from 'components/UI/Modal/MobileModal'
import SearchPage from 'pages/app/mobile/search'
import { ContactCompanySearchResult, DoorSearchResult } from 'models/companies'
import { ProductPrediction } from 'models/deals'
import { SamplesApi } from 'services/samplesApi'
import { SampleRequestCreateUpdate } from 'models/samples'
import FbButton from 'components/FbUI/FbButton'
import { MobileSelectLocationsModal } from 'components/Deals/MobileEditDealModal/MobileSelectLocationsModal'
import { FbLink } from 'components/FbUI/FbLink'
import { phoneNumberZodValidor } from 'utils/zodValidators'
import { handleFormError } from 'utils/handleFormError'
import { MobileContactShippingStep } from 'components/Samples/MobileContactShippingStep'
import { MobileRequestDetailsStep } from 'components/Samples/MobileRequestDetailsStep'
import { MobileProductSelectionStep } from 'components/Samples/MobileProductSelectionStep'
import { MobileSelectProductVariantsStep } from 'components/Samples/MobileProductSelectVariantsStep'
import { useGetVariantsCatalog } from 'services/products_api'
import { MobileSampleReviewStep } from 'components/Samples/MobileSampleReview'
import { XCircle } from 'lucide-react'

// ----- Steps -----
enum Step {
  CHOOSE_COMPANY = 0,
  CONTACT_SHIPPING = 1,
  REQUEST_DETAILS = 2,
  CHOOSE_PRODUCT = 3,
  SELECT_VARIANTS = 4,
  PRODUCT_SELECTIONS = 5,
  REVIEW = 6,
}

const STEP_TITLES: Record<Step, string> = {
  [Step.CHOOSE_COMPANY]: 'Choose a Company',
  [Step.CONTACT_SHIPPING]: 'Contact & Shipping',
  [Step.REQUEST_DETAILS]: 'Request Details',
  [Step.CHOOSE_PRODUCT]: 'Choose a product',
  [Step.SELECT_VARIANTS]: 'Select Product Variants',
  [Step.PRODUCT_SELECTIONS]: 'Product Selections',
  [Step.REVIEW]: 'Review & Submit',
}

// ----- Schema -----
const sampleRequestSchema = z
  .object({
    contact_company_id: z.number().optional(),
    chain_id: z.number().optional().nullable(),
    door_proxy_id: z.number().optional().nullable(),
    date_needed: z.string().min(1, 'Date needed is required'),
    fulfillment_option: z.string().min(1, 'Fulfillment option is required'),
    distributor_id: z.number().optional().nullable(),
    contact_id: z.number().optional().nullable(),
    contact_first_name: z.string().min(1, 'First name is required'),
    contact_last_name: z.string().min(1, 'Last name is required'),
    contact_email: z
      .string()
      .email('Valid email is required')
      .min(1, 'Contact email is required'),
    contact_phone: z.union([phoneNumberZodValidor, z.null(), z.literal('')]),
    company_name: z.string().optional(),
    shipping_address_line1: z.string().min(1, 'Address is required'),
    shipping_address_line2: z.string().optional(),
    shipping_city: z.string().min(1, 'City is required'),
    shipping_state: z.string().min(1, 'State is required'),
    shipping_zip: z
      .string()
      .min(1, 'ZIP code is required')
      .max(5, 'ZIP code must be 5 digits')
      .regex(/^\d{5}$/, 'ZIP code must be 5 digits'),
    shipping_country: z.string().optional(),
    special_instructions: z.string().optional(),
    line_items: z
      .array(
        z.object({
          variant_id: z.number().min(1, 'Product is required'),
          quantity: z.number().min(1, 'Quantity must be at least 1'),
          variant_name: z.string().optional(),
          variant_sku: z.string().optional(),
          variant_format: z.string().optional(),
        })
      )
      .min(1, 'At least one product is required'),
    apply_to: z
      .union([z.literal('entire_company'), z.literal('select_locations')])
      .optional(),
    door_ids: z.array(z.number()).optional(),
  })
  .superRefine((data, ctx) => {
    const hasCompanyId =
      data.contact_company_id !== undefined &&
      data.contact_company_id !== null &&
      data.contact_company_id > 0
    const hasChainId = data.chain_id !== undefined && data.chain_id !== null

    if (!hasCompanyId && !hasChainId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Company is required',
        path: ['contact_company_id'],
      })
    }
  })

type SampleFormValues = z.infer<typeof sampleRequestSchema>

type InitialDoorData = {
  name: string
  address: string
  city: string
  state: string
  zipcode: string
  companyName?: string
}

// ----- Simple product list (same workflow as Create Deal) -----
type SelectProductProps = {
  chainProxyId: number | undefined
  onClick: (prediction: ProductPrediction) => void
}

function SelectProduct({ chainProxyId, onClick }: SelectProductProps) {
  const api = apiService()
  // Only show products that have variants for sample requests
  const { data: predictions } = api.useGetProductPredictions({
    chainProxyId,
    hasVariants: true,
  })

  return (
    <div>
      {predictions?.map((p) => (
        <div
          key={p.product.id}
          onClick={() => onClick(p)}
          className="w-full bg-white rounded-lg shadow-md p-3 border border-gray-200 mb-3 cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors"
        >
          Product: <span className="font-bold">{p.product.name}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * MobileCreateSample
 * - Step 0: Choose company/door
 * - Step 1: Contact + Shipping
 * - Step 2: Request details
 * - Step 3: Choose product
 * - Step 4: Select product variants
 * - Step 5: Product selections (quantities)
 * - Step 6: Review & Submit
 */
export function MobileCreateSamplePage() {
  const api = apiService()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const sampleRequestIdParam = searchParams.get('sample_request_id')
  const sampleRequestId = sampleRequestIdParam
    ? Number(sampleRequestIdParam)
    : undefined

  const preselectedChainId = searchParams.get('chain_id')
    ? Number(searchParams.get('chain_id'))
    : undefined

  const [selectedSearchResult, setSelectedSearchResult] = useState<
    ContactCompanySearchResult | DoorSearchResult | undefined
  >()
  const [selectedPrediction, setSelectedPrediction] = useState<
    ProductPrediction | undefined
  >()

  // Track which product we're currently adding variants for (from Product Selections step)
  const [variantProductContext, setVariantProductContext] = useState<{
    productId: number
    productName: string
  } | null>(null)

  // Use the context product if set, otherwise fallback to selected prediction
  const selectedProductId =
    variantProductContext?.productId ?? selectedPrediction?.product.id

  const { data: variantsCatalog } = useGetVariantsCatalog({
    filters: selectedProductId ? { product: [selectedProductId] } : {},
    page: 1,
    page_size: 100, // enough to show all variants for a product
    enabled: !!selectedProductId,
  })

  const [, setInitialDoorData] = useState<InitialDoorData | undefined>()
  const [showLocationModal, setShowLocationModal] = useState(false)
  const [currentStep, setCurrentStep] = useState<Step>(Step.CHOOSE_COMPANY)

  const isDoorSelected =
    selectedSearchResult && 'chain_proxy_id' in selectedSearchResult

  const chainId =
    selectedSearchResult && !isDoorSelected
      ? Number(selectedSearchResult.item_id)
      : preselectedChainId

  const { data: chainData } = api.useGetChain(chainId)

  const restaurantId =
    selectedSearchResult && isDoorSelected
      ? String(selectedSearchResult.item_id)
      : undefined

  const { data: doorProxy } = api.useGetPlaceV2(restaurantId)

  const chainProxyId = isDoorSelected
    ? doorProxy?.chain_proxy?.id
    : chainData?.contact_company_id

  const formMethods = useForm<SampleFormValues>({
    resolver: zodResolver(sampleRequestSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      company_name: '',
      contact_company_id: 0,
      chain_id: null,
      door_proxy_id: isDoorSelected ? (doorProxy?.id ?? null) : null,
      date_needed: '',
      fulfillment_option: '',
      distributor_id: null,
      contact_id: null,
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
      contact_phone: '',
      shipping_address_line1: '',
      shipping_address_line2: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
      shipping_country: 'US',
      special_instructions: '',
      line_items: [],
      apply_to: 'entire_company',
      door_ids: [],
    },
  })

  const {
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    reset,
    trigger,
    watch,
    setValue,
    getValues,
  } = formMethods

  // All variants as options (no backend change)
  // const { data: variantOptions } = api.useGetProductVariantsOptions()

  const variantsForStep =
    variantsCatalog?.results.map((v) => ({
      id: v.id,
      name: v.name,
      sku: v.sku,
      gtin: v.gtin ?? undefined,
      format: v.format_pack_size ?? undefined,
    })) ?? []

  // ----- Prefill shipping from door -----
  useEffect(() => {
    if (doorProxy && !sampleRequestId) {
      const addr = doorProxy.restaurant?.address ?? ''
      const city = doorProxy.restaurant.city ?? ''
      const state = doorProxy.restaurant.state ?? ''
      const zip = doorProxy.restaurant.address_info.zip ?? ''

      setInitialDoorData({
        name: doorProxy.restaurant.restaurant_name ?? '',
        address: addr,
        city,
        state,
        zipcode: zip,
        companyName: doorProxy.chain_proxy?.name,
      })

      setValue('shipping_address_line1', addr)
      setValue('shipping_city', city)
      setValue('shipping_state', state)
      setValue('shipping_zip', zip)
      setValue('company_name', doorProxy.chain_proxy?.name ?? '')
    }
  }, [doorProxy, sampleRequestId, setValue])

  // Triggers validation at Review
  useEffect(() => {
    if (currentStep === Step.REVIEW) {
      void trigger() // validate all fields with zodResolver
    }
  }, [currentStep, trigger])

  // ----- Submit mutation -----
  const mutation = useMutation({
    mutationFn: async (values: SampleFormValues) => {
      const payload: SampleRequestCreateUpdate = {
        contact_company_id: values.contact_company_id,
        date_needed: values.date_needed,
        fulfillment_option: values.fulfillment_option as any,
        contact_first_name: values.contact_first_name,
        contact_last_name: values.contact_last_name,
        contact_email: values.contact_email,
        contact_phone: values.contact_phone || '',
        shipping_address_line1: values.shipping_address_line1,
        shipping_address_line2: values.shipping_address_line2,
        shipping_city: values.shipping_city,
        shipping_state: values.shipping_state,
        shipping_zip: values.shipping_zip,
        shipping_country: values.shipping_country,
        line_items: values.line_items,
        ...(values.chain_id && { chain_id: values.chain_id }),
        ...(values.door_proxy_id && { door_proxy_id: values.door_proxy_id }),
        ...(values.distributor_id && { distributor_id: values.distributor_id }),
        ...(values.contact_id && { contact_id: values.contact_id }),
        ...(values.special_instructions && {
          special_instructions: values.special_instructions,
        }),
      }

      if (sampleRequestId) {
        return SamplesApi.updateSampleRequest(sampleRequestId, payload)
      }
      return SamplesApi.createSampleRequest(payload)
    },
    onSuccess: () => {
      toast.success(
        sampleRequestId
          ? 'Sample request updated successfully'
          : 'Sample request created successfully'
      )
      void queryClient.invalidateQueries({
        queryKey: ['samples-table'],
        exact: false,
      })
      navigate(-1)
    },
    onError: (err: any) => {
      handleFormError(err, setError)
    },
  })

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  // optional apply_to / door_ids
  const applyTo = watch('apply_to' as any)
  const doorIds = watch('door_ids' as any)

  useEffect(() => {
    if (applyTo === 'select_locations' && chainProxyId) {
      setShowLocationModal(true)
    }
  }, [applyTo, chainProxyId])

  if (showLocationModal && chainProxyId) {
    return (
      <MobileSelectLocationsModal
        title="Create A Sample"
        initialDoorIds={doorIds || []}
        chainProxyId={chainProxyId}
        onCancel={() => setShowLocationModal(false)}
        onSubmit={(selectedDoorIds: number[]) => {
          setValue('door_ids' as any, selectedDoorIds)
          setShowLocationModal(false)
        }}
      />
    )
  }

  const handleCompanyDoorClick = (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => {
    setSelectedSearchResult(result)
    setCurrentStep(Step.CONTACT_SHIPPING)
  }

  useEffect(() => {
    if (!selectedSearchResult) return

    if (isDoorSelected && doorProxy?.chain_proxy?.id) {
      setValue('contact_company_id', doorProxy.chain_proxy.id)
      setValue('company_name', doorProxy.chain_proxy.name ?? '')
    } else if (!isDoorSelected && chainData?.contact_company_id) {
      setValue('contact_company_id', chainData.contact_company_id)
      setValue('company_name', selectedSearchResult.name ?? '')
      setValue('chain_id', Number(selectedSearchResult.item_id))
    }
  }, [selectedSearchResult, isDoorSelected, chainData, doorProxy, setValue])

  const handlePredictionClick = (prediction: ProductPrediction) => {
    setSelectedPrediction(prediction)
    setCurrentStep(Step.SELECT_VARIANTS)
  }

  const handleNext = async () => {
    let fieldsToValidate: string[] = []

    if (currentStep === Step.CHOOSE_COMPANY) {
      const companyId = getValues('contact_company_id')
      if (!companyId) {
        setError('contact_company_id', {
          type: 'manual',
          message: 'Company is required',
        })
        return
      }
    } else if (currentStep === Step.CONTACT_SHIPPING) {
      fieldsToValidate = [
        'contact_first_name',
        'contact_last_name',
        'contact_email',
        'shipping_address_line1',
        'shipping_city',
        'shipping_state',
        'shipping_zip',
      ]
    } else if (currentStep === Step.REQUEST_DETAILS) {
      fieldsToValidate = ['date_needed', 'fulfillment_option']
    } else if (currentStep === Step.PRODUCT_SELECTIONS) {
      fieldsToValidate = ['line_items']
    }

    if (fieldsToValidate.length) {
      const valid = await trigger(fieldsToValidate as any)
      if (!valid) return
    }

    setCurrentStep((s) => Math.min(s + 1, Step.REVIEW))
  }

  const handleBack = () => {
    // if you're on the first step, behave like Cancel
    if (currentStep <= Step.CHOOSE_COMPANY) {
      handleCancel()
      return
    }

    // From variants step, go back to Product Selections if we came from there
    if (currentStep === Step.SELECT_VARIANTS) {
      if (variantProductContext) {
        // Came from Product Selections step via "Add Variant"
        setVariantProductContext(null)
        setCurrentStep(Step.PRODUCT_SELECTIONS)
        return
      }
      // Came from initial product selection, go back to product list
      setSelectedPrediction(undefined)
      setCurrentStep(Step.CHOOSE_PRODUCT)
      return
    }

    setCurrentStep((s) => Math.max(s - 1, Step.CHOOSE_COMPANY))
  }

  const handleCancel = () => {
    reset()
    setSelectedSearchResult(undefined)
    setSelectedPrediction(undefined)
    setCurrentStep(Step.CHOOSE_COMPANY)
    navigate('/')
  }

  return (
    <MobileModal
      handleBack={handleCancel}
      backLabel="Cancel"
      backIcon={<XCircle size={16} />}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a Sample Request
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {STEP_TITLES[currentStep]}
          </h1>
          {currentStep !== Step.REVIEW && selectedSearchResult?.name && (
            <div className="text-xs text-fb-green-800 mt-2">
              <FbLink
                to={`${
                  isDoorSelected
                    ? (selectedSearchResult as DoorSearchResult).chain_proxy_id
                    : selectedSearchResult.item_id
                }`}
              >
                {isDoorSelected
                  ? (selectedSearchResult as DoorSearchResult).chain_name
                  : selectedSearchResult.name}
              </FbLink>
            </div>
          )}

          {currentStep === Step.REVIEW && (
            <div className="text-[9px] text-grey-500 mt-2">
              Please review all information bellow. If you need to make changes,
              use the back button to return to previous steps.
            </div>
          )}
        </div>
      }
      footer={
        currentStep >= Step.CONTACT_SHIPPING ? (
          <div className="flex gap-4 p-2 justify-between">
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="secondary"
                onClick={handleBack}
              >
                {currentStep === Step.CHOOSE_COMPANY ? 'Cancel' : 'Back'}
              </FbButton>
            </div>
            <div className="w-1/2">
              <FbButton
                className="w-full"
                type="button"
                size="mdlg"
                variant="primary"
                onClick={currentStep === Step.REVIEW ? onSubmit : handleNext}
                disabled={
                  currentStep === Step.REVIEW && (!isValid || isSubmitting)
                }
              >
                {currentStep === Step.REVIEW
                  ? 'Submit'
                  : currentStep === Step.PRODUCT_SELECTIONS
                    ? 'Review'
                    : 'Next'}
              </FbButton>
            </div>
          </div>
        ) : (
          <div />
        )
      }
    >
      <FormProvider {...formMethods}>
        {currentStep === Step.CHOOSE_COMPANY &&
          !selectedSearchResult &&
          !preselectedChainId && (
            <div className="w-full">
              <SearchPage hideBack onClick={handleCompanyDoorClick} />
            </div>
          )}

        {currentStep === Step.CONTACT_SHIPPING && <MobileContactShippingStep />}

        {currentStep === Step.REQUEST_DETAILS && (
          <div className="p-2">
            <MobileRequestDetailsStep />
          </div>
        )}

        {currentStep === Step.CHOOSE_PRODUCT && (
          <div className="p-2">
            <SelectProduct
              chainProxyId={chainProxyId}
              onClick={handlePredictionClick}
            />
          </div>
        )}

        {currentStep === Step.SELECT_VARIANTS && selectedProductId && (
          <MobileSelectProductVariantsStep
            productId={selectedProductId}
            productName={
              variantProductContext?.productName ??
              selectedPrediction?.product.name ??
              ''
            }
            variants={variantsForStep}
          />
        )}

        {currentStep === Step.PRODUCT_SELECTIONS && (
          <MobileProductSelectionStep
            onAddVariant={(productId, productName) => {
              setVariantProductContext({ productId, productName })
              setCurrentStep(Step.SELECT_VARIANTS)
            }}
            onSelectProduct={() => {
              setVariantProductContext(null)
              setSelectedPrediction(undefined)
              setCurrentStep(Step.CHOOSE_PRODUCT)
            }}
          />
        )}

        {currentStep === Step.REVIEW && <MobileSampleReviewStep />}
      </FormProvider>
    </MobileModal>
  )
}
